
// Utilitaires de performance ultra-optimisés pour MAISON ZEUS™

// Lazy loading d'images avec intersection observer optimisé
export const createImageObserver = () => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }

  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            // Préchargement avec animation fluide
            img.style.opacity = '0';
            img.style.transition = 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
            
            // Chargement progressif
            const tempImg = new Image();
            tempImg.onload = () => {
              img.src = tempImg.src;
              img.removeAttribute('data-src');
              img.classList.remove('lazy');
              img.classList.add('loaded');
              img.style.opacity = '1';
            };
            tempImg.src = img.dataset.src;
          }
        }
      });
    },
    {
      rootMargin: '50px 0px',
      threshold: 0.1,
    }
  );
};

// Debounce ultra-optimisé
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate?: boolean
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Throttle optimisé pour les événements de scroll
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  let lastFunc: NodeJS.Timeout;
  let lastRan: number;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      lastRan = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func(...args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

// Préchargement d'images avec priorité
export const preloadImage = (src: string, priority: 'high' | 'low' = 'low'): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (priority === 'high') {
      img.loading = 'eager';
      img.fetchPriority = 'high';
    } else {
      img.loading = 'lazy';
      img.fetchPriority = 'low';
    }
    
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

// Préchargement en lot avec gestion d'erreurs
export const preloadImages = async (urls: string[], priority: 'high' | 'low' = 'low'): Promise<void[]> => {
  const promises = urls.map(url => 
    preloadImage(url, priority).catch(error => {
      console.warn(`Image preload failed: ${url}`, error);
      return Promise.resolve();
    })
  );
  return Promise.all(promises);
};

// LocalStorage sécurisé avec compression
export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      const item = localStorage.getItem(key);
      if (item && item.startsWith('compressed:')) {
        return JSON.parse(item.substring(11));
      }
      return item;
    } catch (error) {
      console.warn('LocalStorage getItem failed:', error);
      return null;
    }
  },
  
  setItem: (key: string, value: string, compress: boolean = false): boolean => {
    try {
      let finalValue = value;
      if (compress && value.length > 1000) {
        finalValue = 'compressed:' + JSON.stringify(value);
      }
      localStorage.setItem(key, finalValue);
      return true;
    } catch (error) {
      console.warn('LocalStorage setItem failed:', error);
      // Nettoyage automatique en cas d'espace insuffisant
      try {
        const keys = Object.keys(localStorage);
        if (keys.length > 0) {
          localStorage.removeItem(keys[0]);
          localStorage.setItem(key, value);
          return true;
        }
      } catch (retryError) {
        console.warn('LocalStorage retry failed:', retryError);
      }
      return false;
    }
  },
  
  removeItem: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn('LocalStorage removeItem failed:', error);
      return false;
    }
  },
  
  clear: (): boolean => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.warn('LocalStorage clear failed:', error);
      return false;
    }
  }
};

// Fetch optimisé avec retry et timeout
export const optimizedFetch = async (
  url: string,
  options: RequestInit = {},
  timeout: number = 10000,
  retries: number = 2
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const fetchOptions: RequestInit = {
    ...options,
    signal: controller.signal,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  for (let i = 0; i <= retries; i++) {
    try {
      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response;
    } catch (error) {
      if (i === retries) {
        clearTimeout(timeoutId);
        throw new Error(`Request failed after ${retries + 1} attempts: ${error.message}`);
      }
      
      // Backoff exponentiel
      const delay = Math.min(500 * Math.pow(2, i), 2000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw new Error('Max retries exceeded');
};

// Monitoring de performance
export const performanceMonitor = {
  mark: (name: string) => {
    if ('performance' in window && 'mark' in performance) {
      performance.mark(name);
    }
  },
  
  measure: (name: string, startMark: string, endMark?: string) => {
    if ('performance' in window && 'measure' in performance) {
      try {
        performance.measure(name, startMark, endMark);
        const measure = performance.getEntriesByName(name)[0];
        const duration = measure.duration.toFixed(2);
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`⚡ ${name}: ${duration}ms`);
          
          if (measure.duration > 500) {
            console.warn(`🐌 Performance lente détectée: ${name} a pris ${duration}ms`);
          }
        }
        
        return measure.duration;
      } catch (error) {
        console.warn('Performance measure failed:', error);
        return 0;
      }
    }
    return 0;
  },
  
  getNavigationTiming: () => {
    if ('performance' in window) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      return {
        dns: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
        tcp: Math.round(navigation.connectEnd - navigation.connectStart),
        request: Math.round(navigation.responseStart - navigation.requestStart),
        response: Math.round(navigation.responseEnd - navigation.responseStart),
        dom: Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart),
        load: Math.round(navigation.loadEventEnd - navigation.loadEventStart),
        total: Math.round(navigation.loadEventEnd - navigation.navigationStart),
      };
    }
    return null;
  }
};

// Monitoring mémoire
export const getMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    return {
      used: Math.round(memory.usedJSHeapSize / 1048576),
      total: Math.round(memory.totalJSHeapSize / 1048576),
      limit: Math.round(memory.jsHeapSizeLimit / 1048576),
      percentage: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100),
    };
  }
  return null;
};

// Détection qualité de connexion
export const getConnectionQuality = () => {
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData,
    };
  }
  return null;
};

// Chargement adaptatif selon la connexion
export const adaptiveLoading = () => {
  const connection = getConnectionQuality();
  if (connection) {
    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      document.documentElement.style.setProperty('--image-quality', '0.6');
      document.documentElement.style.setProperty('--animation-duration', '0.1s');
      return 'low';
    } else if (connection.effectiveType === '3g') {
      document.documentElement.style.setProperty('--image-quality', '0.8');
      document.documentElement.style.setProperty('--animation-duration', '0.5s');
      return 'medium';
    }
  }
  document.documentElement.style.setProperty('--image-quality', '1');
  document.documentElement.style.setProperty('--animation-duration', '1s');
  return 'high';
};

// Ajout de resource hints
export const addResourceHints = () => {
  const head = document.head;
  
  const preconnectDomains = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
    'https://readdy.ai',
    'https://static.readdy.ai',
  ];
  
  preconnectDomains.forEach(domain => {
    if (!document.querySelector(`link[href="${domain}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      link.crossOrigin = 'anonymous';
      head.appendChild(link);
    }
  });
  
  const dnsPrefetchDomains = [
    'https://api.stripe.com',
  ];
  
  dnsPrefetchDomains.forEach(domain => {
    if (!document.querySelector(`link[href="${domain}"]`)) {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      head.appendChild(link);
    }
  });
};

// Optimisation des animations
export const optimizeAnimations = () => {
  // Réduction des animations pour l'accessibilité
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.documentElement.style.setProperty('--animation-duration', '0.01ms');
    document.documentElement.style.setProperty('--transition-duration', '0.01ms');
    console.log('🎭 Animations réduites pour l\'accessibilité');
  }
  
  // Optimisation pour les appareils peu puissants
  const memory = getMemoryUsage();
  if (memory && memory.used > 100) {
    document.documentElement.style.setProperty('--animation-complexity', 'simple');
    console.log('🎭 Animations simplifiées pour les performances');
  }
};

// Injection CSS critique
export const injectCriticalCSS = () => {
  const criticalCSS = `
    .animate-glow-text {
      animation: glow-text 2s ease-in-out infinite alternate;
    }
    
    @keyframes glow-text {
      from { text-shadow: 0 0 8px #FF8A00, 0 0 15px #FF8A00; }
      to { text-shadow: 0 0 15px #FF8A00, 0 0 25px #FF8A00; }
    }
    
    .animate-pulse-glow {
      animation: pulse-glow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
    
    @keyframes pulse-glow {
      0%, 100% { opacity: 1; box-shadow: 0 0 15px rgba(255, 138, 0, 0.4); }
      50% { opacity: 0.9; box-shadow: 0 0 25px rgba(255, 138, 0, 0.6); }
    }
    
    .btn-3d {
      transform: translateZ(0);
      transition: all 0.25s ease;
    }
    
    .btn-3d:hover {
      transform: translateY(-3px) translateZ(0);
      box-shadow: 0 8px 20px rgba(255, 138, 0, 0.2);
    }
  `;
  
  const style = document.createElement('style');
  style.textContent = criticalCSS;
  document.head.appendChild(style);
};

// Initialisation des optimisations
export const initPerformanceOptimizations = () => {
  console.log('🚀 Initialisation des optimisations MAISON ZEUS™...');
  
  // Resource hints
  addResourceHints();
  
  // CSS critique
  injectCriticalCSS();
  
  // Optimisation animations
  optimizeAnimations();
  
  // Chargement adaptatif
  const quality = adaptiveLoading();
  console.log(`📶 Qualité de connexion: ${quality}`);
  
  // Monitoring en développement
  if (process.env.NODE_ENV === 'development') {
    // Monitoring mémoire
    setInterval(() => {
      const memory = getMemoryUsage();
      if (memory && memory.percentage > 75) {
        console.warn('🚨 Utilisation mémoire élevée:', memory);
      }
    }, 30000);
    
    // Monitoring performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const timing = performanceMonitor.getNavigationTiming();
        if (timing) {
          console.log('📊 Timing de navigation:', timing);
          
          if (timing.total > 2000) {
            console.warn('🐌 Chargement lent détecté:', timing.total + 'ms');
          }
        }
      }, 1000);
    });
  }
  
  console.log('✅ Optimisations de performance initialisées');
};

// Utilitaires panier optimisés
export const cartUtils = {
  getCart: () => {
    try {
      const savedCart = localStorage.getItem('vybe_cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  },
  
  setCart: (cart: any[]) => {
    try {
      localStorage.setItem('vybe_cart', JSON.stringify(cart));
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: cart }));
      return true;
    } catch {
      return false;
    }
  },
  
  addToCart: (item: any) => {
    const cart = cartUtils.getCart();
    const existingItem = cart.find((cartItem: any) => 
      cartItem.id === item.id && cartItem.selectedSize === item.selectedSize
    );
    
    if (existingItem) {
      existingItem.quantity += item.quantity || 1;
    } else {
      cart.push({ ...item, cartId: Date.now().toString() });
    }
    
    cartUtils.setCart(cart);
    return cart;
  },
  
  removeFromCart: (cartId: string) => {
    const cart = cartUtils.getCart();
    const updatedCart = cart.filter((item: any) => item.cartId !== cartId);
    cartUtils.setCart(updatedCart);
    return updatedCart;
  },
  
  updateQuantity: (cartId: string, quantity: number) => {
    const cart = cartUtils.getCart();
    const item = cart.find((cartItem: any) => cartItem.cartId === cartId);
    
    if (item) {
      if (quantity <= 0) {
        return cartUtils.removeFromCart(cartId);
      } else {
        item.quantity = quantity;
        cartUtils.setCart(cart);
      }
    }
    
    return cart;
  },
  
  clearCart: () => {
    cartUtils.setCart([]);
  },
  
  getCartTotal: () => {
    const cart = cartUtils.getCart();
    return cart.reduce((total: number, item: any) => 
      total + (item.price * item.quantity), 0
    );
  },
  
  getCartCount: () => {
    const cart = cartUtils.getCart();
    return cart.reduce((count: number, item: any) => 
      count + item.quantity, 0
    );
  }
};

// Optimisation des images
export const optimizeImages = () => {
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    // Ajout du lazy loading natif
    if (!img.loading) {
      img.loading = 'lazy';
    }
    
    // Optimisation du décodage
    img.decoding = 'async';
    
    // Ajout de tailles responsives si manquantes
    if (!img.sizes && img.srcset) {
      img.sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
    }
  });
};

// Nettoyage mémoire
export const cleanupMemory = () => {
  // Nettoyage des event listeners orphelins
  const elements = document.querySelectorAll('[data-cleanup]');
  elements.forEach(element => {
    const events = element.getAttribute('data-cleanup')?.split(',') || [];
    events.forEach(event => {
      element.removeEventListener(event.trim(), () => {});
    });
  });
  
  // Force garbage collection si disponible
  if ('gc' in window) {
    (window as any).gc();
  }
};

// Optimisation des paiements
export const paymentUtils = {
  // Validation des données de paiement
  validatePaymentData: (data: any) => {
    const errors: string[] = [];
    
    if (!data.productName || data.productName.trim().length === 0) {
      errors.push('Nom du produit requis');
    }
    
    if (!data.price || data.price <= 0) {
      errors.push('Prix invalide');
    }
    
    if (!data.quantity || data.quantity <= 0) {
      errors.push('Quantité invalide');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Optimisation des requêtes de paiement
  createOptimizedPayment: async (paymentData: any) => {
    const validation = paymentUtils.validatePaymentData(paymentData);
    if (!validation.isValid) {
      throw new Error(`Données invalides: ${validation.errors.join(', ')}`);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(paymentData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Timeout: La requête a pris trop de temps');
      }
      
      throw error;
    }
  },

  // Gestion des erreurs de paiement avec retry
  handlePaymentWithRetry: async (paymentData: any, maxRetries = 2) => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await paymentUtils.createOptimizedPayment(paymentData);
      } catch (error) {
        console.error(`Tentative ${attempt + 1} échouée:`, error);
        
        if (attempt === maxRetries) {
          throw new Error(`Échec après ${maxRetries + 1} tentatives: ${error.message}`);
        }
        
        // Délai exponentiel entre les tentatives
        const delay = Math.min(1000 * Math.pow(2, attempt), 3000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
};

// Optimisation des formulaires
export const formUtils = {
  // Validation en temps réel optimisée
  createValidator: (rules: Record<string, (value: any) => string | null>) => {
    const validationCache = new Map();
    
    return (field: string, value: any) => {
      const cacheKey = `${field}:${JSON.stringify(value)}`;
      
      if (validationCache.has(cacheKey)) {
        return validationCache.get(cacheKey);
      }
      
      const rule = rules[field];
      const result = rule ? rule(value) : null;
      
      validationCache.set(cacheKey, result);
      
      // Nettoyage du cache après 5 minutes
      setTimeout(() => {
        validationCache.delete(cacheKey);
      }, 300000);
      
      return result;
    };
  },

  // Soumission de formulaire optimisée
  submitForm: async (url: string, data: any, options: {
    timeout?: number;
    retries?: number;
    onProgress?: (progress: number) => void;
  } = {}) => {
    const { timeout = 10000, retries = 1, onProgress } = options;
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        onProgress?.(0);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        onProgress?.(25);
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(data).toString(),
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        onProgress?.(75);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        onProgress?.(100);
        return response;
        
      } catch (error) {
        if (attempt === retries) {
          throw error;
        }
        
        const delay = Math.min(500 * Math.pow(2, attempt), 2000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
};

// Optimisation des animations
export const animationUtils = {
  // Gestionnaire d'animations performant
  createAnimationManager: () => {
    const activeAnimations = new Set();
    let rafId: number | null = null;
    
    const update = () => {
      if (activeAnimations.size > 0) {
        rafId = requestAnimationFrame(update);
      } else {
        rafId = null;
      }
    };
    
    return {
      add: (animation: () => void) => {
        activeAnimations.add(animation);
        if (!rafId) {
          rafId = requestAnimationFrame(update);
        }
      },
      
      remove: (animation: () => void) => {
        activeAnimations.delete(animation);
      },
      
      clear: () => {
        activeAnimations.clear();
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      }
    };
  },

  // Optimisation des transitions CSS
  optimizeTransitions: () => {
    const style = document.createElement('style');
    style.textContent = `
      .optimized-transition {
        will-change: transform, opacity;
        transform: translateZ(0);
        backface-visibility: hidden;
        perspective: 1000px;
      }
      
      .gpu-accelerated {
        transform: translate3d(0, 0, 0);
        will-change: transform;
      }
      
      @media (prefers-reduced-motion: reduce) {
        .optimized-transition,
        .gpu-accelerated {
          transition: none !important;
          animation: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }
};

// Optimisation du chargement des images
export const imageOptimizer = {
  // Chargement progressif d'images
  loadProgressively: (src: string, placeholder?: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      
      // Chargement du placeholder en premier
      if (placeholder) {
        const placeholderImg = new Image();
        placeholderImg.onload = () => {
          img.style.opacity = '0';
          img.style.transition = 'opacity 0.3s ease';
        };
        placeholderImg.src = placeholder;
      }
      
      img.onload = () => {
        img.style.opacity = '1';
        resolve(img);
      };
      
      img.onerror = () => {
        reject(new Error(`Failed to load image: ${src}`));
      };
      
      // Optimisations
      img.loading = 'lazy';
      img.decoding = 'async';
      img.src = src;
    });
  },

  // Redimensionnement automatique selon la densité d'écran
  getOptimalImageUrl: (baseUrl: string, width: number, height: number) => {
    const dpr = window.devicePixelRatio || 1;
    const optimalWidth = Math.round(width * Math.min(dpr, 2)); // Limite à 2x
    const optimalHeight = Math.round(height * Math.min(dpr, 2));
    
    // Si l'URL contient déjà des paramètres de taille, les remplacer
    if (baseUrl.includes('width=') && baseUrl.includes('height=')) {
      return baseUrl
        .replace(/width=\d+/, `width=${optimalWidth}`)
        .replace(/height=\d+/, `height=${optimalHeight}`);
    }
    
    // Sinon, ajouter les paramètres
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}width=${optimalWidth}&height=${optimalHeight}`;
  }
};

// Monitoring avancé des performances
export const advancedMonitoring = {
  // Surveillance de la mémoire en temps réel
  startMemoryMonitoring: (callback: (usage: any) => void, interval = 30000) => {
    const monitor = () => {
      const memory = getMemoryUsage();
      if (memory) {
        callback(memory);
        
        // Alerte si utilisation > 80%
        if (memory.percentage > 80) {
          console.warn('🚨 Utilisation mémoire critique:', memory);
          
          // Nettoyage automatique
          cleanupMemory();
        }
      }
    };
    
    monitor(); // Première mesure
    const intervalId = setInterval(monitor, interval);
    
    return () => clearInterval(intervalId);
  },

  // Surveillance des Core Web Vitals
  measureWebVitals: () => {
    const vitals = {
      LCP: 0, // Largest Contentful Paint
      FID: 0, // First Input Delay
      CLS: 0, // Cumulative Layout Shift
    };

    // LCP
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      vitals.LCP = lastEntry.startTime;
      console.log('📊 LCP:', vitals.LCP.toFixed(2) + 'ms');
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // FID
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        vitals.FID = entry.processingStart - entry.startTime;
        console.log('📊 FID:', vitals.FID.toFixed(2) + 'ms');
      });
    }).observe({ entryTypes: ['first-input'] });

    // CLS
    let clsValue = 0;
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      vitals.CLS = clsValue;
      console.log('📊 CLS:', vitals.CLS.toFixed(4));
    }).observe({ entryTypes: ['layout-shift'] });

    return vitals;
  }
};

// Initialisation complète des optimisations
export const initCompleteOptimizations = () => {
  console.log('🚀 Initialisation complète des optimisations MAISON ZEUS™...');
  
  // Optimisations de base
  initPerformanceOptimizations();
  
  // Optimisations avancées
  animationUtils.optimizeTransitions();
  
  // Monitoring en production
  if (process.env.NODE_ENV === 'production') {
    advancedMonitoring.measureWebVitals();
    
    const stopMemoryMonitoring = advancedMonitoring.startMemoryMonitoring((usage) => {
      if (usage.percentage > 90) {
        console.error('🚨 Mémoire critique, nettoyage forcé');
        cleanupMemory();
      }
    });
    
    // Nettoyage au déchargement de la page
    window.addEventListener('beforeunload', () => {
      stopMemoryMonitoring();
      cleanupMemory();
    });
  }
  
  console.log('✅ Optimisations complètes initialisées');
};
